// Ulepszona obsługa błędów dla PlexiSystem
import { Component, ErrorInfo, ReactNode } from 'react';

// Typy błędów
export enum ErrorType {
  NETWORK = 'NETWORK',
  AUTH = 'AUTH',
  VALIDATION = 'VALIDATION',
  SERVER = 'SERVER',
  CLIENT = 'CLIENT',
  UNKNOWN = 'UNKNOWN'
}

// Klasa rozszerzonego błędu
export class AppError extends Error {
  public type: ErrorType;
  public statusCode?: number;
  public details?: any;
  public timestamp: Date;
  public userMessage: string;

  constructor(
    message: string,
    type: ErrorType = ErrorType.UNKNOWN,
    statusCode?: number,
    details?: any
  ) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.statusCode = statusCode;
    this.details = details;
    this.timestamp = new Date();
    this.userMessage = this.getUserFriendlyMessage();
  }

  private getUserFriendlyMessage(): string {
    switch (this.type) {
      case ErrorType.NETWORK:
        return 'Wystąpił problem z połączeniem. Sprawdź swoje połączenie internetowe.';
      case ErrorType.AUTH:
        return 'Sesja wygasła. Zaloguj się ponownie.';
      case ErrorType.VALIDATION:
        return 'Wprowadzone dane są nieprawidłowe. Sprawdź formularz.';
      case ErrorType.SERVER:
        return 'Wystąpił błąd serwera. Spróbuj ponownie za chwilę.';
      case ErrorType.CLIENT:
        return 'Wystąpił nieoczekiwany błąd. Odśwież stronę.';
      default:
        return 'Wystąpił nieznany błąd. Skontaktuj się z administratorem.';
    }
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      type: this.type,
      statusCode: this.statusCode,
      details: this.details,
      timestamp: this.timestamp,
      userMessage: this.userMessage
    };
  }
}

// Logger błędów
export class ErrorLogger {
  private errors: AppError[] = [];
  private maxErrors: number = 100;

  log(error: AppError | Error): void {
    const appError = error instanceof AppError 
      ? error 
      : new AppError(error.message, ErrorType.UNKNOWN);
    
    this.errors.push(appError);
    
    // Ogranicz liczbę przechowywanych błędów
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(-this.maxErrors);
    }

    // Log do konsoli w trybie deweloperskim
    if (process.env.NODE_ENV === 'development') {
      console.error('🔴 Error logged:', appError.toJSON());
    }

    // Możesz tutaj dodać wysyłanie błędów do zewnętrznego serwisu
    // this.sendToExternalService(appError);
  }

  getErrors(): AppError[] {
    return [...this.errors];
  }

  getErrorsByType(type: ErrorType): AppError[] {
    return this.errors.filter(error => error.type === type);
  }

  clearErrors(): void {
    this.errors = [];
  }

  getLastError(): AppError | null {
    return this.errors[this.errors.length - 1] || null;
  }

  private sendToExternalService(error: AppError): void {
    // Implementacja wysyłania błędów do Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production') {
      // fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(error.toJSON())
      // }).catch(() => {
      //   console.error('Failed to send error to external service');
      // });
    }
  }
}

export const errorLogger = new ErrorLogger();

// Helper do obsługi błędów API
export async function handleApiError(error: any): Promise<never> {
  let appError: AppError;

  if (error.response) {
    // Błąd z odpowiedzią serwera
    const status = error.response.status;
    const data = error.response.data;

    if (status === 401) {
      appError = new AppError(
        'Unauthorized',
        ErrorType.AUTH,
        status,
        data
      );
    } else if (status === 422 || status === 400) {
      appError = new AppError(
        'Validation error',
        ErrorType.VALIDATION,
        status,
        data
      );
    } else if (status >= 500) {
      appError = new AppError(
        'Server error',
        ErrorType.SERVER,
        status,
        data
      );
    } else {
      appError = new AppError(
        `API error: ${status}`,
        ErrorType.CLIENT,
        status,
        data
      );
    }
  } else if (error.request) {
    // Brak odpowiedzi
    appError = new AppError(
      'No response from server',
      ErrorType.NETWORK
    );
  } else {
    // Inny błąd
    appError = new AppError(
      error.message || 'Unknown error',
      ErrorType.UNKNOWN
    );
  }

  errorLogger.log(appError);
  throw appError;
}

// Retry helper z exponential backoff
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  let lastError: Error | null = null;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      
      if (i < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, i);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error('Max retries exceeded');
}

// Circuit breaker pattern
export class CircuitBreaker {
  private failures: number = 0;
  private lastFailureTime: number = 0;
  private state: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED';
  
  constructor(
    private threshold: number = 5,
    private timeout: number = 60000 // 1 minuta
  ) {}

  async execute<T>(fn: () => Promise<T>): Promise<T> {
    if (this.state === 'OPEN') {
      if (Date.now() - this.lastFailureTime > this.timeout) {
        this.state = 'HALF_OPEN';
      } else {
        throw new AppError(
          'Circuit breaker is open',
          ErrorType.CLIENT
        );
      }
    }

    try {
      const result = await fn();
      if (this.state === 'HALF_OPEN') {
        this.reset();
      }
      return result;
    } catch (error) {
      this.recordFailure();
      throw error;
    }
  }

  private recordFailure(): void {
    this.failures++;
    this.lastFailureTime = Date.now();
    
    if (this.failures >= this.threshold) {
      this.state = 'OPEN';
    }
  }

  private reset(): void {
    this.failures = 0;
    this.state = 'CLOSED';
    this.lastFailureTime = 0;
  }

  getState(): string {
    return this.state;
  }
}

// Global error handler
export function setupGlobalErrorHandlers(): void {
  // Obsługa nieobsłużonych Promise rejection
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    errorLogger.log(
      new AppError(
        'Unhandled promise rejection',
        ErrorType.CLIENT,
        undefined,
        event.reason
      )
    );
    event.preventDefault();
  });

  // Obsługa globalnych błędów JavaScript
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
    errorLogger.log(
      new AppError(
        event.message,
        ErrorType.CLIENT,
        undefined,
        {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      )
    );
  });
}

// Eksport wszystkich narzędzi
export default {
  AppError,
  ErrorType,
  errorLogger,
  handleApiError,
  retryWithBackoff,
  CircuitBreaker,
  setupGlobalErrorHandlers
};
